import axiosDefault, { AxiosRequestConfig } from 'axios'
import config from 'config'
import { getAccessToken, routeToLoginOnDeadSession } from 'lib/authToken'
import {
  currentSessionGeneration,
  isStaleAccountSession,
  isStaleAccountSessionError,
  StaleAccountSessionError
} from 'lib/accountSessionState'

const axios = axiosDefault.create({
  baseURL: config.apiHost
})

type TrackedConfig = AxiosRequestConfig & { _retriedOn401?: boolean; _sessionGeneration?: number }

const rejectAsStale: AxiosRequestConfig['adapter'] = () => Promise.reject(new StaleAccountSessionError())

/* Tags each request with the current account-session number the moment it goes out, before a switch
 * to another account can move that number on. A retry keeps the number it first carried; if the
 * account has already changed, the request is turned back before axios sends it to the server. */
axios.interceptors.request.use(
  (config) => {
    if (!config.headers) config.headers = {}
    const accessToken = getAccessToken()
    config.headers.Authorization = accessToken ? `Bearer ${accessToken}` : ''

    const tracked = config as TrackedConfig
    if (tracked._sessionGeneration === undefined) {
      tracked._sessionGeneration = currentSessionGeneration()
    }
    if (isStaleAccountSession(tracked._sessionGeneration)) {
      tracked.adapter = rejectAsStale
    }
    return config
  },
  undefined,
  { synchronous: true }
)

const bearerToken = (header?: unknown): string | null => {
  if (typeof header !== 'string' || !header) return null
  return header.startsWith('Bearer ') ? header.slice('Bearer '.length) : header
}

/* How long to give Frontegg to finish refreshing the token before treating a 401 as a session that
 * has ended — covers the gap right after the laptop wakes, before its refresh timers have fired. */
const TOKEN_REFRESH_GRACE_MS = 1000

/* A response whose account session ended before it made it back (the user switched accounts). */
const isStaleResponse = (config?: TrackedConfig): boolean =>
  config?._sessionGeneration !== undefined && isStaleAccountSession(config._sessionGeneration)

axios.interceptors.response.use(
  (response) => {
    if (isStaleResponse(response.config as TrackedConfig)) {
      return Promise.reject(new StaleAccountSessionError())
    }
    return response
  },
  async (error) => {
    const originalRequest = error?.config as TrackedConfig | undefined
    const status = error?.response?.status

    // Any stale outcome is a quiet cancellation: never a dead session, never retried.
    if (isStaleAccountSessionError(error) || isStaleResponse(originalRequest)) {
      return Promise.reject(new StaleAccountSessionError())
    }

    /* Recover from a 401 (unauthorized). Frontegg may still be finishing a token refresh — for
     * instance just after the laptop wakes — so wait a brief grace period, then retry once with the
     * refreshed token. Only a token still unchanged after that means the session is really dead. */
    if (status === 401 && originalRequest) {
      if (!originalRequest._retriedOn401) {
        originalRequest._retriedOn401 = true
        const sentToken = bearerToken(originalRequest.headers?.Authorization)
        if (getAccessToken() === sentToken) {
          await new Promise((resolve) => setTimeout(resolve, TOKEN_REFRESH_GRACE_MS))
        }
        const freshToken = getAccessToken()
        if (freshToken && freshToken !== sentToken) {
          return axios(originalRequest)
        }
      }
      routeToLoginOnDeadSession()
    }

    return Promise.reject(error)
  }
)

export const unauthenticatedAxios = axiosDefault.create({
  baseURL: config.apiHost
})

const isAxiosError = axiosDefault.isAxiosError

export { axios, isAxiosError }
export default axios
